import { useEffect, useState } from "react";

type Variant = "roof" | "solar" | "map" | "contact";

interface PageIntroProps {
  /** Kept for API compatibility; the intro now plays on every visit. */
  storageKey?: string;
  variant: Variant;
}

/**
 * Branded splash loader (deep-navy) played once per session on service hubs.
 * Draws an animated line-art logo (roof house or solar sun) instead of a static logo.
 * No tagline, no copyright line.
 */
export function PageIntro({ storageKey, variant }: PageIntroProps) {
  const [mounted, setMounted] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [done, setDone] = useState(false);

  const DURATION = 2000;
  const FADE = 500;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      document.documentElement.classList.contains("a11y-reduce-motion");
    if (reduced) {
      setDone(true);
      return;
    }
    setMounted(true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t1 = window.setTimeout(() => setLeaving(true), DURATION);
    const t2 = window.setTimeout(() => {
      setDone(true);
      document.body.style.overflow = prevOverflow;
    }, DURATION + FADE);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      document.body.style.overflow = prevOverflow;
    };
  }, [storageKey]);

  if (done || !mounted) return null;

  return (
    <div
      aria-hidden="true"
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-primary transition-opacity ease-out ${
        leaving ? "opacity-0" : "opacity-100"
      }`}
      style={{ transitionDuration: `${FADE}ms` }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.45) 100%)",
        }}
      />

      <div className="relative flex flex-col items-center">
        <div className="mb-6 h-[160px] w-[200px]">
          {variant === "roof" ? (
            <RoofArt />
          ) : variant === "solar" ? (
            <SolarArt />
          ) : variant === "map" ? (
            <MapArt />
          ) : (
            <ContactArt />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="loader-dot" style={{ animationDelay: "0ms" }} />
          <span className="loader-dot" style={{ animationDelay: "200ms" }} />
          <span className="loader-dot" style={{ animationDelay: "400ms" }} />
        </div>
      </div>

      <style>{`
        .loader-dot {
          height: 8px; width: 8px; border-radius: 9999px; background: rgba(255,255,255,0.9);
          animation: loaderDot 1.2s ease-in-out infinite;
        }
        @keyframes loaderDot {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
        .pi-draw {
          stroke: #ffffff;
          fill: none;
          stroke-width: 4;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: var(--len, 400);
          stroke-dashoffset: var(--len, 400);
          animation: piDraw 1.2s ease-out forwards;
        }
        .pi-fill {
          opacity: 0;
          animation: piFade 500ms ease-out forwards;
        }
        .pi-ray {
          transform-origin: 100px 90px;
          opacity: 0;
          animation: piRay 500ms ease-out forwards;
        }
        @keyframes piDraw { to { stroke-dashoffset: 0; } }
        @keyframes piFade { to { opacity: 1; } }
        @keyframes piRay {
          from { opacity: 0; transform: scale(0.6); }
          to { opacity: 1; transform: scale(1); }
        }
        .pi-drop {
          opacity: 0;
          animation: piDrop 700ms cubic-bezier(0.22,1,0.36,1) forwards;
        }
        @keyframes piDrop {
          0% { opacity: 0; transform: translateY(-26px) scale(0.8); }
          70% { opacity: 1; transform: translateY(2px) scale(1.05); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .pi-pulse {
          transform-origin: center;
          opacity: 0;
          animation: piPulse 1.4s ease-out infinite;
        }
        @keyframes piPulse {
          0% { opacity: 0.7; transform: scale(0.4); }
          100% { opacity: 0; transform: scale(1.6); }
        }
      `}</style>
    </div>
  );
}

function RoofArt() {
  // Draw inverted V roof first, then chimney, then two windows fade in (matches reference video).
  return (
    <svg viewBox="0 0 200 160" className="h-full w-full">
      {/* Roof line */}
      <path
        d="M20 90 L100 30 L180 90"
        className="pi-draw"
        style={{ ["--len" as never]: 220, animationDelay: "0ms" } as React.CSSProperties}
      />
      {/* Chimney */}
      <path
        d="M55 62 L55 40 L70 40 L70 51"
        className="pi-draw"
        style={{ ["--len" as never]: 60, animationDelay: "700ms" } as React.CSSProperties}
      />
      {/* Windows (accent) */}
      <g style={{ animationDelay: "1000ms" } as React.CSSProperties} className="pi-fill">
        <rect x="82" y="92" width="14" height="14" rx="2" fill="#F97316" />
        <rect x="104" y="92" width="14" height="14" rx="2" fill="#F97316" />
      </g>
    </svg>
  );
}

function SolarArt() {
  // Sun + rays + a small panel below (bespoke solar variant).
  const rays = Array.from({ length: 8 });
  return (
    <svg viewBox="0 0 200 160" className="h-full w-full">
      {/* Sun */}
      <circle
        cx="100"
        cy="70"
        r="26"
        className="pi-draw"
        style={{ ["--len" as never]: 170, animationDelay: "0ms" } as React.CSSProperties}
      />
      {/* Rays */}
      <g>
        {rays.map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          const x1 = 100 + Math.cos(a) * 36;
          const y1 = 70 + Math.sin(a) * 36;
          const x2 = 100 + Math.cos(a) * 52;
          const y2 = 70 + Math.sin(a) * 52;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              className="pi-draw"
              style={
                {
                  ["--len" as never]: 20,
                  animationDelay: `${600 + i * 60}ms`,
                  stroke: "#F97316",
                } as React.CSSProperties
              }
            />
          );
        })}
      </g>
      {/* Panel */}
      <g style={{ animationDelay: "1200ms" } as React.CSSProperties} className="pi-fill">
        <path
          d="M60 130 L140 130 L150 145 L50 145 Z"
          fill="none"
          stroke="#ffffff"
          strokeWidth={3}
          strokeLinejoin="round"
        />
        <line x1="72" y1="130" x2="66" y2="145" stroke="#ffffff" strokeWidth={2} />
        <line x1="100" y1="130" x2="100" y2="145" stroke="#ffffff" strokeWidth={2} />
        <line x1="128" y1="130" x2="134" y2="145" stroke="#ffffff" strokeWidth={2} />
      </g>
    </svg>
  );
}
function MapArt() {
  // California outline drawn in, then a pin drops with a radiating pulse.
  return (
    <svg viewBox="0 0 200 160" className="h-full w-full">
      {/* Simplified California silhouette */}
      <path
        d="M62 18 L92 26 L104 52 L128 78 L140 112 L156 140 L112 140 L96 120 L74 96 L60 62 Z"
        className="pi-draw"
        style={{ ["--len" as never]: 460, animationDelay: "0ms" } as React.CSSProperties}
      />
      {/* Pulse ring under the pin */}
      <circle
        cx="100"
        cy="86"
        r="18"
        fill="none"
        stroke="#F97316"
        strokeWidth={3}
        className="pi-pulse"
        style={{ animationDelay: "1100ms" } as React.CSSProperties}
      />
      {/* Map pin */}
      <g className="pi-drop" style={{ animationDelay: "900ms" } as React.CSSProperties}>
        <path
          d="M100 60 C90 60 82 68 82 78 C82 92 100 108 100 108 C100 108 118 92 118 78 C118 68 110 60 100 60 Z"
          fill="#F97316"
        />
        <circle cx="100" cy="78" r="6" fill="#ffffff" />
      </g>
    </svg>
  );
}

function ContactArt() {
  // Speech bubble drawn in, then a handset and signal waves.
  return (
    <svg viewBox="0 0 200 160" className="h-full w-full">
      <path
        d="M46 40 L154 40 Q164 40 164 50 L164 100 Q164 110 154 110 L104 110 L82 132 L82 110 L46 110 Q36 110 36 100 L36 50 Q36 40 46 40 Z"
        className="pi-draw"
        style={{ ["--len" as never]: 470, animationDelay: "0ms" } as React.CSSProperties}
      />
      <g className="pi-drop" style={{ animationDelay: "900ms" } as React.CSSProperties}>
        <path
          d="M80 62 L92 62 L98 76 L90 82 C94 90 102 98 110 102 L116 94 L130 100 L130 112 C130 116 126 118 122 117 C100 112 80 92 75 70 C74 66 76 62 80 62 Z"
          fill="#F97316"
        />
      </g>
      <g className="pi-fill" style={{ animationDelay: "1300ms" } as React.CSSProperties}>
        <circle cx="100" cy="126" r="3" fill="#ffffff" />
        <circle cx="114" cy="126" r="3" fill="#ffffff" />
        <circle cx="128" cy="126" r="3" fill="#ffffff" />
      </g>
    </svg>
  );
}
